// src/components/MspOs.js
// Bridge between authenticated session + live API data and the MSP OS app.
// Loads all live data on mount, passes it as props to MspOsApp,
// and provides API functions so the app calls the backend instead of
// making direct external API calls.

import React, { useEffect, useState, useCallback, useRef } from "react";
import { useMsal } from "@azure/msal-react";
import { getEmails, archiveEmail, deleteEmail, replyToEmail, fetchEmailBody } from "../api/emailApi";
import {
  getTicketReferenceData,
  lookupContact,
  createTicket,
  getContractsByCompany,
  getContactsByCompany,
  getAssetsByCompany,
  addTicketNote,
  getMyTickets, getUnassignedTickets, getTicketDetail, updateTicket, createTimeEntry, suggestIssueSummary,
  getCompanyTickets, getTicketNotes, getTicketTime,
  getTicketSecondaryResources, addTicketSecondaryResource,
} from "../api/ticketApi";
import MspOsMobile from "./MspOsMobile";
import TeamReporting from "./TeamReporting";
import itGlueApi from "../api/itGlueApi";
import { getCalendarEvents } from "../api/calendarApi";
import { generateBrief, extractTodos, planDay, fetchChaseUp, fetchClientHealth } from "../api/aiApi";
import { getDlpRules, saveDlpRules, previewDlp } from "../api/dlpApi";
import { getFellowStatus, getFellowMeetings, getFellowMeetingNotes, annotateFellowMeeting } from "../api/fellowApi";
import { loadState, saveState, getTodos, toggleTodo, addTodo } from "../api/stateApi";
import { getDattoData, triggerDattoSync } from "../api/dattoApi";
import { getMyItProcessData, triggerMyItProcessSync, setMyItProcessMapping } from "../api/myItProcessApi";
import { saveMeetingPrepQuestions, sendMeetingPrepLink, getMeetingPrepLink } from "../api/meetingPrepApi";
import { loadAtCompanies, triggerSync, getSyncStatus, exportCsv } from "../api/autotaskDataApi";
import { loadPlan, savePlan, loadDayCanvas, saveDayCanvas } from "../api/planApi";
import apiClient from "../api/apiClient";
import { getAutotaskData } from "../api/autotaskDataApi";
import MspOsApp from "./MspOsApp";

// ── Loading screen ──────────────────────────────────────────────────────────

const loadCss = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700&display=swap');
  .load-wrap{width:100vw;height:100vh;background:#f8f9fa;display:flex;flex-direction:column;align-items:center;justify-content:center;font-family:'Inter',sans-serif;}
  .load-card{background:white;border:1px solid #dde3e8;border-radius:12px;padding:40px 48px;min-width:320px;box-shadow:0 4px 6px rgba(0,0,0,0.08);}
  .load-logo{width:44px;height:44px;border-radius:10px;background:linear-gradient(135deg,#2a4365,#0b80a5);display:flex;align-items:center;justify-content:center;font-size:20px;font-weight:700;color:white;font-family:'Inter',sans-serif;margin:0 auto 16px;}
  .load-title{font-weight:700;font-size:17px;color:#202020;margin-bottom:4px;text-align:center;}
  .load-sub{font-size:12px;color:#838383;margin-bottom:28px;text-align:center;}
  .load-steps{display:flex;flex-direction:column;gap:10px;}
  .load-step{display:flex;align-items:center;gap:10px;font-size:12px;padding:8px 12px;border-radius:6px;background:#f8f9fa;border:1px solid #dde3e8;}
  .load-step.ls-done{background:rgba(11,128,165,0.06);border-color:rgba(11,128,165,0.2);color:#0b80a5;}
  .load-step.ls-active{background:rgba(11,128,165,0.04);border-color:rgba(11,128,165,0.15);color:#202020;}
  .load-step.ls-error{background:rgba(231,76,60,0.05);border-color:rgba(231,76,60,0.2);color:#e74c3c;}
  .load-step.ls-wait{color:#838383;}
  .load-spin{width:14px;height:14px;border-radius:50%;border:2px solid #dde3e8;border-top-color:#0b80a5;animation:lspin 0.7s linear infinite;flex-shrink:0;}
  @keyframes lspin{to{transform:rotate(360deg)}}
  .load-icon{width:14px;height:14px;display:flex;align-items:center;justify-content:center;flex-shrink:0;font-size:12px;}
  .load-err{margin-top:16px;padding:10px 12px;border-radius:6px;background:rgba(231,76,60,0.06);border:1px solid rgba(231,76,60,0.2);color:#e74c3c;font-size:11px;text-align:center;}
`;

function LoadingScreen({ steps, error }) {
  return (
    <>
      <style>{loadCss}</style>
      <div className="load-wrap">
        <div className="load-card">
          <div className="load-logo">M</div>
          <div className="load-title">MSP Operating System</div>
          <div className="load-sub">Loading your workspace...</div>
          <div className="load-steps">
            {steps.map((s, i) => (
              <div key={i} className={`load-step ls-${s.status}`}>
                <div className="load-icon">
                  {s.status === "active"  && <div className="load-spin"/>}
                  {s.status === "done"    && "✓"}
                  {s.status === "error"   && "✗"}
                  {s.status === "wait"    && "·"}
                </div>
                {s.label}
              </div>
            ))}
          </div>
          {error && <div className="load-err">{error}</div>}
        </div>
      </div>
    </>
  );
}

// ── Transform live data to match the format MspOsApp expects ───────────────

function transformEmails(graphMessages) {
  if (!graphMessages?.length) return [];
  return graphMessages.map(msg => ({
    id:       msg.id,
    from:     msg.from?.emailAddress?.name  || msg.from?.emailAddress?.address || "Unknown",
    email:    msg.from?.emailAddress?.address || "",
    subj:     msg.subject || "(no subject)",
    // The inbox feed spans a rolling 3 weeks, so a time alone would make an
    // 18-day-old email read as though it had just arrived. Today keeps the
    // time; anything older shows its date.
    time:     (() => {
      const d = new Date(msg.receivedDateTime);
      if (isNaN(d)) return "";
      const now = new Date();
      const sameDay = d.getDate() === now.getDate() && d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
      return sameDay
        ? d.toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" })
        : d.toLocaleDateString("en-AU", { day: "numeric", month: "short" });
    })(),
    priority: msg.importance === "high" ? "urgent" : "normal",
    vip:      false,
    aiSum:    msg.bodyPreview ? msg.bodyPreview.substring(0, 120) : null,
    isRead:   msg.isRead,
    graphId:  msg.id,
  }));
}

function transformCalendarEvents(events, knownDomains=[]) {
  if (!events?.length) return [];
  return events
    .filter(ev => ev.start?.dateTime || ev.start?.date)
    .map(ev => {
      const rawStart = ev.start?.dateTime || ev.start?.date;
      const rawEnd   = ev.end?.dateTime   || ev.end?.date;

      // Graph returns genuine UTC times with timeZone="UTC".
      // Perth is UTC+8 (no DST). Simply add 8 hours.
      // e.g. 03:30 UTC + 8h = 11:30am Perth ✓
      // Note: Graph sends 7 decimal places e.g. "2026-04-21T03:30:00.0000000"
      // which some browsers don't parse correctly — normalise to 3 decimals first.
      const toDisplayTime = (raw) => {
        if (!raw) return { dt: null, timeStr: "09:00", dateStr: null };
        // Normalise fractional seconds to 3 places and ensure Z suffix
        const normalised = raw
          .replace(/(\.\d{3})\d+/, "$1") // trim to 3 decimal places
          .replace(/([+-]\d{2}:\d{2}|Z)?$/, "") // strip any existing tz
          .concat("Z"); // always treat as UTC
        const utcDt = new Date(normalised);
        if (isNaN(utcDt.getTime())) return { dt: null, timeStr: "09:00", dateStr: null };
        const perthMs  = utcDt.getTime() + (8 * 60 * 60 * 1000);
        const perthDt  = new Date(perthMs);
        const hh  = String(perthDt.getUTCHours()).padStart(2, "0");
        const mm  = String(perthDt.getUTCMinutes()).padStart(2, "0");
        const yyyy = perthDt.getUTCFullYear();
        const mo  = String(perthDt.getUTCMonth() + 1).padStart(2, "0");
        const dd  = String(perthDt.getUTCDate()).padStart(2, "0");
        return { dt: perthDt, timeStr: `${hh}:${mm}`, dateStr: `${yyyy}-${mo}-${dd}` };
      };

      const { dt: startDt, timeStr: startTime, dateStr: localDateStr } = toDisplayTime(rawStart);
      const { dt: endDt } = toDisplayTime(rawEnd);

      const durationMins = endDt && startDt && !isNaN(endDt) && !isNaN(startDt)
        ? Math.round((endDt.getTime() - startDt.getTime()) / 60000)
        : 60;

      return {
        id:           ev.id,
        title:        ev.subject || "Untitled",
        date:         startDt,
        localDateStr,
        startTime,
        duration:     durationMins,
        client:       extractClientFromEvent(ev, knownDomains),
        tier:         "vcio",
        type:         detectMeetingType(ev.subject || ""),
        attendees:    ev.attendees || [],
        iCalUID:      ev.iCalUID || null,
      };
    })
    .filter(ev => ev.localDateStr) // must have a valid date
    .filter(ev => isClientMeeting(ev))
    .sort((a, b) => {
      // Sort by date then time
      if (a.localDateStr !== b.localDateStr) return a.localDateStr.localeCompare(b.localDateStr);
      return a.startTime.localeCompare(b.startTime);
    });
}

function extractClientFromEvent(ev, knownDomains=[]) {
  const ownDomains = ["datatechsolutions", "datatech", "microsoft", "teams.ms"];

  // 1. Try to extract client from meeting title — most reliable for recurring meetings
  // Patterns: "CBH Group — Monthly Review", "Monthly Tech Catchup — Hotel Rottnest"
  const title = ev.subject || "";
  const dashMatch = title.match(/(?:—|--)\s*(.+?)\s*(?:—|--|$)/);
  const prefixMatch = title.match(/^(.+?)\s*(?:—|--|-)\s*(?:monthly|catchup|review|it |tech )/i);
  if (dashMatch && dashMatch[1] && !dashMatch[1].toLowerCase().includes("monthly")) {
    const candidate = dashMatch[1].trim();
    if (candidate.length > 2 && candidate.length < 60) return candidate;
  }
  if (prefixMatch && prefixMatch[1]) {
    const candidate = prefixMatch[1].trim();
    if (candidate.length > 2 && candidate.length < 60) return candidate;
  }

  // 2. Try external attendees
  const externalAttendees = ev.attendees?.filter(a => {
    const email = (a.emailAddress?.address || "").toLowerCase();
    return !ownDomains.some(d => email.includes(d));
  }) || [];

  if (externalAttendees.length > 0) {
    // Try domain matching FIRST — more reliable than display name for identifying companies.
    // Display name is a person name ("John Smith"), not a company name.
    for (const attendee of externalAttendees) {
      const email = attendee.emailAddress?.address || "";
      const domain = email.split("@")[1] || "";
      if (domain && knownDomains.length) {
        const matched = knownDomains.find(kd => kd.domain && (domain === kd.domain || domain.endsWith("." + kd.domain)));
        if (matched) return matched.client;
      }
    }
    // No domain match — fall back to display name, then domain-derived name
    const displayName = externalAttendees[0].emailAddress?.name || "";
    if (displayName && !displayName.includes("@")) return displayName;
    const email = externalAttendees[0].emailAddress?.address || "";
    const domain = email.split("@")[1] || "";
    const domainName = domain.split(".")[0] || "";
    const clientName = domainName.replace(/-/g, " ").split(" ").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
    return clientName || "External";
  }

  // 3. Check organiser
  const orgEmail = (ev.organizer?.emailAddress?.address || "").toLowerCase();
  const orgDomain = orgEmail.split("@")[1] || "";
  if (!ownDomains.some(d => orgDomain.includes(d)) && orgDomain) {
    return orgDomain.split(".")[0].charAt(0).toUpperCase() + orgDomain.split(".")[0].slice(1);
  }

  return "Internal";
}

function detectMeetingType(title) {
  const t = (title || "").toLowerCase();
  if (t.includes("qbr") || t.includes("quarterly") || t.includes("strategy") || t.includes("annual review")) return "qbr";
  if (
    t.includes("monthly") || t.includes("check-in") || t.includes("checkin") ||
    t.includes("managed service") || t.includes("management meeting") ||
    t.includes("it review") || t.includes("service review") ||
    t.includes("tech catchup") || t.includes("catchup") || t.includes("catch up") ||
    t.includes("it management") || t.includes("msp meeting") || t.includes("support meeting") ||
    t.includes("review meeting") || t.includes("status meeting") || t.includes("team meeting")
  ) return "monthly";
  return "adhoc";
}

// Filter out non-meeting calendar entries (leave, personal, admin)
function isClientMeeting(ev) {
  const title = (ev.subject || "").toLowerCase();
  // Exclude personal/admin/leave entries
  const excludeKeywords = [
    "leave", "away", "holiday", "annual leave", "sick",
    "payg", "ato", "tax", "pays due", "birthday", "anniversary",
    "lunch", "dinner", "personal", "dentist", "doctor",
    "reminder", "follow up", "todo", "task",
  ];
  if (excludeKeywords.some(kw => title.includes(kw))) return false;
  // Include meeting if it has attendees OR if the title strongly suggests a client meeting
  const hasAttendees = ev.attendees && ev.attendees.length > 0;
  const titleSuggestsClientMeeting = [
    "monthly", "catchup", "catch up", "review", "check-in", "checkin",
    "management meeting", "tech meeting", "it meeting", "qbr", "quarterly"
  ].some(kw => title.includes(kw));
  return hasAttendees || titleSuggestsClientMeeting;
}

function transformAtReference(ref) {
  if (!ref) return null;
  return {
    companies:     ref.companies     || [],
    queues:        ref.queues        || [],
    statuses:      ref.statuses      || [],
    workTypes:     ref.workTypes     || [],
    resources:     ref.resources     || [],
    contracts:     ref.contracts     || [],
    issueTypes:    ref.issueTypes    || [],
    subIssueTypes: ref.subIssueTypes || [],
    sources:       ref.sources       || [],
  };
}

// ── Main component ──────────────────────────────────────────────────────────

export default function MspOs({ userInfo }) {
  const { instance, accounts } = useMsal();

  const [steps, setSteps] = useState([
    { label: "Connecting to backend...",           status: "active" },
    { label: "Loading saved workspace...",         status: "wait"   },
    { label: "Loading inbox from M365...",         status: "wait"   },
    { label: "Loading calendar events...",         status: "wait"   },
    { label: "Loading Autotask reference data...", status: "wait"   },
    { label: "Checking Fellow connection...",      status: "wait"   },
  ]);
  const [ready,         setReady]       = useState(false);
  const [loadError,     setLoadError]   = useState(null);
  const [liveEmails,    setLiveEmails]  = useState([]);
  const [syncedCompanies, setSyncedCompanies] = useState([]);
  const [liveEvents,    setLiveEvents]  = useState([]);
  const [dattoData,     setDattoData]   = useState({ sites:[], devices:[], alerts:[], syncStatus:null });
  const [myItProcessData, setMyItProcessData] = useState({ summaries:[], clients:[], reviews:[], overdue:[], findings:[], recommendations:[], initiatives:[], meetings:[], syncStatus:null });
  const [atRef,         setAtRef]       = useState(null);
  const [sentimentData, setSentimentData] = useState(null);
  const [fellowReady,   setFellowReady] = useState(false);
  const isFirstLoad = !localStorage.getItem("msp-os-loaded");
  const [lastEmailRefresh, setLastEmailRefresh] = useState(null);
  const [emailRefreshing,  setEmailRefreshing]  = useState(false);
  const pollIntervalRef   = useRef(null);
  const sentimentPollerRef = useRef(null);
  const rawCalEventsRef    = useRef([]);
  const clientDomainsRef   = useRef([]);
  const syncIntervalRef   = useRef(null);
  const runAtSyncRef      = useRef(null);
  const [atSyncing,    setAtSyncing]    = useState(false);
  const [atDashData,   setAtDashData]   = useState({ tickets:[], ticketNotes:[], resources:[], statuses:[] });
  const [savedState,    setSavedState]  = useState(null);
  const [stateLoaded,   setStateLoaded] = useState(false);
  const [syncStatus,    setSyncStatus]  = useState(null);
  const saveTimerRef = useRef(null);
  const pendingStateRef = useRef(null);
  const lastSaveAtRef = useRef(0);

  const updateStep = (index, status) =>
    setSteps(prev => prev.map((s, i) => i === index ? { ...s, status } : s));

  useEffect(() => {
    async function loadAll() {
      try {
        // Step 0 — backend health (retry up to 3x for post-deploy cold starts)
        let healthOk = false;
        for (let attempt = 0; attempt < 3; attempt++) {
          try {
            const health = await fetch(`${process.env.REACT_APP_BACKEND_URL}/health`);
            if (health.ok) { healthOk = true; break; }
          } catch (e) { /* backend restarting */ }
          if (attempt < 2) await new Promise(r => setTimeout(r, 3000)); // wait 3s between retries
        }
        if (!healthOk) throw new Error("Backend not reachable");
        updateStep(0, "done");

        // Step 1 — load saved state
        updateStep(1, "active");
        try {
          const result = await loadState();
          if (result?.state) {
            setSavedState(result.state);
          }
          setStateLoaded(true);
          updateStep(1, "done");
        } catch (e) {
          console.warn("State load failed:", e.message);
          setStateLoaded(true); // still render app even if state load fails
          updateStep(1, "error");
        }

        // Step 2 — emails
        updateStep(2, "active");
        try {
          const msgs = await getEmails();
          setLiveEmails(transformEmails(msgs));
          updateStep(2, "done");
        } catch (e) {
          console.warn("Email load failed:", e.message);
          updateStep(2, "error");
        }

        // Step 3 — calendar
        updateStep(3, "active");
        try {
          const evs = await getCalendarEvents();
          // Store raw events — will be re-transformed once clientDomains loads
          rawCalEventsRef.current = evs;
          const transformed = transformCalendarEvents(evs, clientDomainsRef.current || []);
          console.log("[Calendar] transformed events:", transformed.length, "| sample:", transformed.slice(0,3).map(m=>({title:m.title,type:m.type,client:m.client})));
          setLiveEvents(transformed);
          updateStep(3, "done");
        } catch (e) {
          console.warn("Calendar load failed:", e.message);
          updateStep(3, "error");
        }

        // Step 4 — Autotask reference data
        updateStep(4, "active");
        try {
          const ref = await getTicketReferenceData();
          setAtRef(transformAtReference(ref));
          updateStep(4, "done");
        } catch (e) {
          console.warn("Autotask load failed:", e.message);
          updateStep(4, "error");
        }

        // Load AT dashboard data in background (non-blocking)
        Promise.all([
          getAutotaskData("tickets"),
          getAutotaskData("ticketNotes"),
          getAutotaskData("resources"),
          getAutotaskData("ticketStatuses"),
        ]).then(([t, n, r, s]) => {
          setAtDashData({
            tickets:      t?.data || [],
            ticketNotes:  n?.data || [],
            resources:    r?.data || [],
            statuses:     s?.data || [],
          });
        }).catch(e => console.warn("AT dashboard pre-load failed:", e.message));

        // Step 5 — Fellow status
        updateStep(5, "active");
        try {
          const connected = await getFellowStatus();
          setFellowReady(connected);
          updateStep(5, connected ? "done" : "error");
        } catch (e) {
          console.warn("Fellow status check failed:", e.message);
          updateStep(5, "error");
        }

        // Mark as loaded so subsequent visits skip the loading screen
        localStorage.setItem("msp-os-loaded", "1");
        setTimeout(() => setReady(true), 500);

        // Start email polling every 2 minutes
        if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = setInterval(async () => {
          try {
            setEmailRefreshing(true);
            const msgs = await getEmails();
            setLiveEmails(transformEmails(msgs));
            setLastEmailRefresh(new Date());
          } catch (e) {
            console.warn("Email poll failed:", e.message);
          } finally {
            setEmailRefreshing(false);
          }
        }, 2 * 60 * 1000); // 2 minutes

        // Background Autotask sync — on login then every 10 minutes
        const runAtSync = async () => {
          setAtSyncing(true);
          try {
            console.log("Background AT sync starting...");
            // Sync companies first so typeahead populates quickly
            await triggerSync("companies");
            const earlyCompanies = await loadAtCompanies();
            if (earlyCompanies?.length) setSyncedCompanies(earlyCompanies);
            // Then full sync
            await triggerSync("all");
            console.log("Background AT sync complete");
          } catch (e) {
            console.warn("Background AT sync failed:", e.message);
          } finally {
            setAtSyncing(false);
          }
          // Always try to reload companies at the end
          try {
            const fresh = await loadAtCompanies();
            if (fresh?.length) setSyncedCompanies(fresh);
          } catch (e) {
            console.warn("Companies reload failed:", e.message);
          }
        };

        // Store for external calls (manual refresh button)
        runAtSyncRef.current = runAtSync;

        // ── Step 1: Load cached sentiment immediately from blob ──────────────
        // This is instant — just a blob read, no AI calls
        apiClient.get("/api/ai/sentiment")
          .then(r => {
            if (r.data?.results?.length) setSentimentData(r.data);
          })
          .catch(e => console.warn("Sentiment cache load:", e.message));

        // ── Step 1b: Trigger background sentiment build once per day ─────────
        // Only polls while build is actively in progress (partial:true)
        // Stops the moment partial:false — never polls stable data
        apiClient.post("/api/ai/sentiment/build")
          .then(r => {
            if (r.data?.status !== "building") return; // cache fresh — no polling needed

            let polls = 0;
            let lastAnalysedAt = null;

            const startPolling = () => {
              if (sentimentPollerRef.current) clearInterval(sentimentPollerRef.current);
              sentimentPollerRef.current = setInterval(() => {
                polls++;
                apiClient.get("/api/ai/sentiment")
                  .then(r2 => {
                    if (!r2.data?.results?.length) return;
                    // Only update state if data actually changed (new analysedAt)
                    if (r2.data.analysedAt !== lastAnalysedAt) {
                      lastAnalysedAt = r2.data.analysedAt;
                      setSentimentData(r2.data);
                    }
                    // Stop polling once build fully complete
                    if (!r2.data.partial) {
                      clearInterval(sentimentPollerRef.current);
                      sentimentPollerRef.current = null;
                    }
                  })
                  .catch(() => {});
                // Safety: stop after 25 minutes regardless
                if (polls >= 50) {
                  clearInterval(sentimentPollerRef.current);
                  sentimentPollerRef.current = null;
                }
              }, 30000);
            };

            startPolling();
          })
          .catch(e => console.warn("Sentiment build trigger:", e.message));

        // ── Step 1c: Pre-compile Weekly Ticket Reports for any due today ─────
        precompileWeeklyReports(apiClient).catch(() => {});

        // ── Step 1d: Load Datto RMM data from blob cache ─────────────────────
        getDattoData()
          .then(d => { if (d?.sites) setDattoData(d); })
          .catch(() => {}); // Datto may not be configured yet

        // ── Step 1e: Load myITprocess data from blob cache ───────────────────
        getMyItProcessData()
          .then(d => { if (d?.summaries) setMyItProcessData(d); })
          .catch(() => {}); // myITprocess may not be configured yet

        // ── Step 2: Run AT sync, then reload dashboard data only ─────────────
        // Sentiment is decoupled — no longer rebuilt after sync
        runAtSync().then(() => {
          Promise.all([
            getAutotaskData("tickets"),
            getAutotaskData("ticketNotes"),
            getAutotaskData("resources"),
            getAutotaskData("ticketStatuses"),
          ]).then(([t, n, r, s]) => {
            setAtDashData({
              tickets:     t?.data || [],
              ticketNotes: n?.data || [],
              resources:   r?.data || [],
              statuses:    s?.data || [],
            });
          }).catch(() => {});
        });

      } catch (err) {
        setLoadError(`${err.message} — check backend is running`);
        setSteps(prev =>
          prev.map(s => s.status === "active" ? { ...s, status: "error" } : s)
        );
      }
    }
    // Always load data fully — just hide the loading screen visually on repeat visits
    loadAll();

    // Cleanup polling and sync on unmount
    const pollInterval = pollIntervalRef.current;
    const syncInterval = syncIntervalRef.current;
    return () => {
      if (pollInterval) clearInterval(pollInterval);
      if (syncInterval) clearInterval(syncInterval);
      if (sentimentPollerRef.current) clearInterval(sentimentPollerRef.current);
    };
  }, []);

  // ── API functions passed to MspOsApp ──────────────────────────────────────

  const handleCreateTicket = useCallback(async (ticketData) => {
    return await createTicket(ticketData);
  }, []);

  const handleArchiveEmail = useCallback(async (graphId) => {
    return await archiveEmail(graphId);
  }, []);

  const handleDeleteEmail = useCallback(async (graphId) => {
    return await deleteEmail(graphId);
  }, []);

  const handleReplyEmail = useCallback(async (graphId, htmlBody) => {
    return await replyToEmail(graphId, htmlBody);
  }, []);

  const handleFetchEmailBody = useCallback(async (graphId) => {
    return await fetchEmailBody(graphId);
  }, []);

  const handleAddTicketNote = useCallback(async (ticketId, payload) => {
    return await addTicketNote(ticketId, payload);
  }, []);

  const handleGenerateBrief = useCallback(async (promptData) => {
    return await generateBrief(promptData);
  }, []);

  const handlePlanDay = useCallback(async (prompt) => {
    return await planDay(prompt);
  }, []);

  const handleFetchChaseUp = useCallback(async () => {
    // Pass AT companies + resources so the backend can DLP-scrub known names
    const companies  = atRef?.companies  || [];
    const resources  = atRef?.resources  || [];
    return await fetchChaseUp({ atCompanies: companies, atResources: resources });
  }, [atRef]);

  const handleFetchClientHealth = useCallback(async ({ dattoAlerts, dattoSites } = {}) => {
    // Managed clients only (tier set and not "Adhoc"), pass Datto context for per-client health
    const managedIds = atRef?.companies
      ?.filter(c => c._tier && c._tier !== "Adhoc")
      .map(c => c.id) || [];
    return await fetchClientHealth({ managedCompanyIds: managedIds, dattoAlerts, dattoSites });
  }, [atRef]);

  // Pre-compile weekly ticket reports for companies with reports due today
  // Reads AT company list and triggers background compilation for each
  async function precompileWeeklyReports(client) {
    try {
      const cc = await client.get("/api/autotask-data/companies");
      const companies = cc?.data?.data || cc?.data || [];
      const today = new Date().toISOString().slice(0, 10);
      // Fire precompile for each active company (backend handles cache check internally)
      // Limit to 20 to avoid hammering on first load
      const targets = companies.slice(0, 20);
      await Promise.allSettled(
        targets.map(c =>
          client.get(`/api/reports/weekly-ticket/${c.id}/precompile?date=${today}`)
            .catch(() => {})
        )
      );
    } catch (e) {
      console.warn("Weekly report precompile:", e.message);
    }
  }

  // Meeting prep helpers — used via onTriggerDattoSync prop chain
  const handleSendMeetingPrepLink = useCallback(async ({ companyId, meetingDate, contactEmail, contactName, meetingTitle }) => { // eslint-disable-line no-unused-vars
    return await sendMeetingPrepLink(companyId, { meetingDate, contactEmail, contactName, meetingTitle });
  }, []);

  const handleGetMeetingPrepLink = useCallback(async (companyId, meetingDate) => { // eslint-disable-line no-unused-vars
    return await getMeetingPrepLink(companyId, meetingDate);
  }, []);

  const handleSaveMeetingPrepQuestions = useCallback(async (companyId, questions) => { // eslint-disable-line no-unused-vars
    return await saveMeetingPrepQuestions(companyId, questions);
  }, []);

  const handleRunSentiment = useCallback(async ({ days=30, force=true }={}) => {
    // Manual Refresh button — calls /sentiment/refresh which rebuilds synchronously
    // and streams back the full result when done
    const url = `/api/ai/sentiment/refresh${force ? "?refresh=1" : ""}`;
    const response = await apiClient.get(url);
    return response.data;
  }, []);

  const handleRefreshAtSync = useCallback(async () => {
    if (runAtSyncRef.current) await runAtSyncRef.current();
  }, []);

  // Returns a fresh access token for use in the report URL
  const handleGetReportToken = useCallback(async () => {
    try {
      const account = instance.getAllAccounts()[0];
      if (!account) return null;
      const tokenResp = await instance.acquireTokenSilent({
        scopes: ["api://1660c30a-9608-404d-b6a3-3fee98626edf/access_as_user"],
        account,
      });
      return tokenResp.accessToken;
    } catch (e) {
      console.warn("getReportToken failed:", e.message);
      return null;
    }
  }, [instance]);

  const handleGetAutotaskData = useCallback(async (entity) => {
    try {
      const result = await getAutotaskData(entity);
      return result?.data || [];
    } catch { return []; }
  }, []);

  const handleLoadDayCanvas = useCallback(async (date) => {
    return await loadDayCanvas(date);
  }, []);

  const handleSaveDayCanvas = useCallback(async (date, blocks) => {
    return await saveDayCanvas(date, blocks);
  }, []);

  const handleLoadAtCompanies = useCallback(async () => {
    return await loadAtCompanies();
  }, []);

  const handleExtractTodos = useCallback(async (notesText, meetingContext) => {
    return await extractTodos(notesText, meetingContext);
  }, []);

  // ── DLP (Data Loss Prevention) handlers ─────────────────────────────────
  const handleDlpFetch = useCallback(async () => {
    try { return await getDlpRules(); }
    catch (e) { console.warn("DLP fetch failed:", e.message); return null; }
  }, []);

  const handleDlpSave = useCallback(async ({ mode, rules }) => {
    return await saveDlpRules({ mode, rules });
  }, []);

  const handleDlpPreview = useCallback(async (text) => {
    return await previewDlp(text);
  }, []);

  const handleLookupContact = useCallback(async (email) => {
    return await lookupContact(email);
  }, []);

  const handleGetContracts = useCallback(async (companyId) => {
    return await getContractsByCompany(companyId);
  }, []);

  const handleGetContacts = useCallback(async (companyId) => {
    return await getContactsByCompany(companyId);
  }, []);

  const handleGetAssets = useCallback(async (companyId) => {
    return await getAssetsByCompany(companyId);
  }, []);

  // ── Re-transform calendar events once AT company data loads ─────────────────
  // clientDomainsRef starts empty at calendar load time (companies not loaded yet).
  // This effect runs after the background AT sync populates syncedCompanies,
  // builds the real domain list, and re-transforms the stored raw calendar events
  // so client names resolve correctly for the Cadence Tactical column.
  useEffect(() => {
    if (!syncedCompanies?.length || !rawCalEventsRef.current?.length) return;

    // Build domain list: AT webAddress → domain, with savedState overrides applied
    const overrides = savedState?.clientDomainOverrides || {};
    const domains = syncedCompanies.map(c => {
      const override = overrides[String(c.id)];
      const derived = c.webAddress
        ? c.webAddress.replace(/^https?:\/\//, "").replace(/^www\./, "").split("/")[0].toLowerCase()
        : "";
      const domain = override || derived;
      return domain ? { client: c.companyName, domain } : null;
    }).filter(Boolean);

    clientDomainsRef.current = domains;

    // Re-transform raw calendar events now that we have a real domain list
    const retransformed = transformCalendarEvents(rawCalEventsRef.current, domains);
    console.log("[Calendar] re-transformed with domains:", domains.length, "| events:", retransformed.length,
      "| sample:", retransformed.slice(0,3).map(m => ({ title: m.title, type: m.type, client: m.client })));
    setLiveEvents(retransformed);
  }, [syncedCompanies, savedState]); // eslint-disable-line react-hooks/exhaustive-deps

  const flushSaveState = useCallback(async () => {
    if (!pendingStateRef.current) return;
    const stateToSave = pendingStateRef.current;
    pendingStateRef.current = null;
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    try {
      await saveState(stateToSave);
      lastSaveAtRef.current = Date.now();
    } catch (e) {
      console.warn("State save failed:", e.message);
      // Restore so the next debounce cycle retries
      pendingStateRef.current = stateToSave;
    }
  }, []);

  const handleSaveState = useCallback((state) => {
    pendingStateRef.current = state;
    const now = Date.now();
    const sinceLastSave = now - lastSaveAtRef.current;

    // Save immediately on the very first call this session, or if it's been
    // more than 5 seconds since the last successful save; otherwise debounce.
    const shouldSaveNow = lastSaveAtRef.current === 0 || sinceLastSave > 5000;

    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);

    if (shouldSaveNow) {
      saveTimerRef.current = setTimeout(() => { flushSaveState(); }, 0);
    } else {
      saveTimerRef.current = setTimeout(() => { flushSaveState(); }, 2000);
    }
  }, [flushSaveState]);

  // Flush pending state when the user navigates away or hides the tab.
  useEffect(() => {
    const flushSync = () => {
      if (!pendingStateRef.current) return;
      const stateToSave = pendingStateRef.current;
      pendingStateRef.current = null;
      try {
        const token = window.__mspToken;
        if (!token) return;
        // fetch with keepalive survives page unload and carries the auth header.
        // stateApi.js uses PUT /api/state — match that here.
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/state`, {
          method: "PUT",
          keepalive: true,
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
          },
          body: JSON.stringify(stateToSave),
        });
      } catch (_e) { /* best-effort */ }
    };

    const handleBeforeUnload = () => { flushSync(); };
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        flushSaveState();
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [flushSaveState]);

  const handleTriggerSync = useCallback(async (entity = "all") => {
    return await triggerSync(entity);
  }, []);

  const handleExportCsv = useCallback(async (entity) => {
    return await exportCsv(entity);
  }, []);

  const handleGetSyncStatus = useCallback(async () => {
    const result = await getSyncStatus();
    setSyncStatus(result?.status);
    return result;
  }, []);

  const handleGetFellowMeetings = useCallback(async () => {
    return await getFellowMeetings();
  }, []);

  const handleGetFellowNotes = useCallback(async (meetingId, title, forceRefresh = false) => {
    return await getFellowMeetingNotes(meetingId, title, forceRefresh);
  }, []);

  const handleAnnotateFellowMeeting = useCallback(async (meetingId, annotations) => {
    return await annotateFellowMeeting(meetingId, annotations);
  }, []);

  const handleSignOut = useCallback(() => {
    localStorage.removeItem("msp-os-loaded");
    instance.logoutRedirect({ account: accounts[0] });
  }, [instance, accounts]);

  // Force re-authentication — clears all MSAL tokens and hard-navigates to root
  // so MSAL init runs fresh and redirects to Azure AD login page.
  const handleForceLogin = useCallback(() => {
    const clearMsalKeys = (storage) => {
      Object.keys(storage)
        .filter(k => k.startsWith("msal.") || k.includes("login.windows") ||
                     k.includes("accesstoken") || k.includes("idtoken") || k.includes("refreshtoken"))
        .forEach(k => storage.removeItem(k));
    };
    clearMsalKeys(sessionStorage);
    clearMsalKeys(localStorage);
    localStorage.removeItem("msp-os-loaded");
    window.location.href = window.location.origin + "/";
  }, []);

  // Auto sign-out after 2 hours of inactivity
  useEffect(() => {
    const TIMEOUT_MS = 2 * 60 * 60 * 1000; // 2 hours
    let timer;
    const resetTimer = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        console.warn("MSP OS: 2 hours of inactivity — signing out");
        handleForceLogin();
      }, TIMEOUT_MS);
    };
    // Reset on any user activity
    const events = ["mousedown", "mousemove", "keydown", "touchstart", "scroll", "click"];
    events.forEach(e => window.addEventListener(e, resetTimer, { passive: true }));
    resetTimer(); // start the timer
    return () => {
      clearTimeout(timer);
      events.forEach(e => window.removeEventListener(e, resetTimer));
    };
  }, [handleForceLogin]);

  if (!ready) {
    // First load — show full branded loading screen
    if (isFirstLoad) {
      return <LoadingScreen steps={steps} error={loadError} />;
    }
    // Subsequent loads — show minimal spinner so there's no flash of broken UI
    return (
      <>
        <style>{loadCss}</style>
        <div className="login-wrap" style={{background:"#f8f9fa"}}>
          <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:14}}>
            <div style={{width:36,height:36,borderRadius:"50%",border:"3px solid #dde3e8",borderTopColor:"#0b80a5",animation:"lspin 0.7s linear infinite"}}/>
            <div style={{fontSize:12,color:"#838383",fontFamily:"Inter,sans-serif"}}>Loading workspace...</div>
          </div>
        </div>
      </>
    );
  }

  /* ── PRINT VIEW ─────────────────────────────────────────────────────────────
     `/?print=team-reporting&resourceId=…&start=…&end=…` renders ONLY the Team
     Reporting sheet — no header, no sidenav, no side panels — so Ctrl-P
     produces the 1:1 handout.

     ⛔ SAME MSAL SESSION, SAME ORIGIN. No share token, no public route. This
     branch sits AFTER the `ready` gate, so the user is authenticated before it
     renders; TeamReporting acquires its own token per request through apiClient,
     so it does not depend on MspOsApp's refresh loop having run — which is what
     used to make a directly-opened tab 401 while looking logged in.

     It is placed BEFORE the stateLoaded gate deliberately: the sheet needs none
     of the app-state blob, and waiting for it only delays the print dialog.

     The app has no router, and this must not introduce one — it is a query
     param read once at bootstrap. */
  if (typeof window !== "undefined"
      && new URLSearchParams(window.location.search).get("print") === "team-reporting") {
    return <TeamReporting />;
  }

  // ── Render MSP OS app with live data ──────────────────────────────────────
  // Wait for state to load before rendering so savedState prop has correct values
  if (!stateLoaded) {
    return (
      <>
        <style>{loadCss}</style>
        <div className="login-wrap" style={{background:"#f8f9fa"}}>
          <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:14}}>
            <div style={{width:36,height:36,borderRadius:"50%",border:"3px solid #dde3e8",borderTopColor:"#0b80a5",animation:"lspin 0.7s linear infinite"}}/>
            <div style={{fontSize:12,color:"#838383",fontFamily:"Inter,sans-serif"}}>Loading workspace...</div>
          </div>
        </div>
      </>
    );
  }

  // Device fork — phone/iPad get mobile view; desktop gets full MspOsApp unchanged.
  // iPadOS 13+ reports as desktop Safari (Macintosh), so also check maxTouchPoints + coarse pointer.
  const isMobileDevice = (() => {
    const ua = navigator.userAgent;
    const phone = /iPhone|Android.*Mobile|Windows Phone/i.test(ua);
    const ipad  = /iPad/i.test(ua) || (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1);
    const coarse = window.matchMedia?.("(pointer: coarse)").matches;
    return phone || ipad || (coarse && window.innerWidth < 1024);
  })();

  if (isMobileDevice) {
    return (
      <MspOsMobile
        userInfo={userInfo}
        atReference={atRef}
        onCreateTicket={handleCreateTicket}
        onAddTicketNote={handleAddTicketNote}
        onLookupContact={handleLookupContact}
        onGetContacts={handleGetContacts}
        onGetContracts={handleGetContracts}
        onGetAssets={handleGetAssets}
        onLoadAtCompanies={handleLoadAtCompanies}
        syncedCompanies={syncedCompanies}
        onSignOut={handleSignOut}
        timelog={{ getMyTickets, getUnassignedTickets, getTicketDetail, updateTicket, createTimeEntry, suggestIssueSummary, getCompanyTickets, getTicketNotes, getTicketTime,
          getSecondaryResources: (ticketId) => getTicketSecondaryResources(ticketId),
          addSecondaryResource: (ticketId, resourceId) => addTicketSecondaryResource(ticketId, resourceId),
          getTodos, toggleTodo, addTodo }}
        itglue={itGlueApi}
      />
    );
  }

  return (
    <MspOsApp
      userInfo={userInfo}
      liveEmails={liveEmails}
      liveCalEvents={liveEvents}
      atReference={atRef}
      fellowConnected={fellowReady}
      onCreateTicket={handleCreateTicket}
      onArchiveEmail={handleArchiveEmail}
      onDeleteEmail={handleDeleteEmail}
      onReplyEmail={handleReplyEmail}
      onFetchEmailBody={handleFetchEmailBody}
      onAddTicketNote={handleAddTicketNote}
      onGenerateBrief={handleGenerateBrief}
      onPlanDay={handlePlanDay}
      onLoadAtCompanies={handleLoadAtCompanies}
      syncedCompanies={syncedCompanies}
      onExtractTodos={handleExtractTodos}
      onFetchChaseUp={handleFetchChaseUp}
      onFetchClientHealth={handleFetchClientHealth}
      onDlpFetch={handleDlpFetch}
      onDlpSave={handleDlpSave}
      onDlpPreview={handleDlpPreview}
      onLookupContact={handleLookupContact}
      onGetContracts={handleGetContracts}
      onGetContacts={handleGetContacts}
      onGetAssets={handleGetAssets}
      onGetFellowMeetings={handleGetFellowMeetings}
      onGetFellowNotes={handleGetFellowNotes}
      onAnnotateFellowMeeting={handleAnnotateFellowMeeting}
      onLoadPlan={loadPlan}
      onSavePlan={savePlan}
      onGetAutotaskData={handleGetAutotaskData}
      onRunSentiment={handleRunSentiment}
      onGetReportToken={handleGetReportToken}
      preloadedSentiment={sentimentData}
      dattoData={dattoData}
      onTriggerDattoSync={async (managedNames=[]) => { const r = await triggerDattoSync(managedNames); return r; }}
      onRefreshDattoData={async () => { const d = await getDattoData(); if (d?.sites) setDattoData(d); return d; }}
      myItProcessData={myItProcessData}
      onTriggerMyItProcessSync={async () => { const r = await triggerMyItProcessSync(); return r; }}
      onRefreshMyItProcessData={async () => { const d = await getMyItProcessData(); if (d?.summaries) setMyItProcessData(d); return d; }}
      onSetMyItProcessMapping={async (mitpId, atId) => {
        const r = await setMyItProcessMapping(mitpId, atId);
        const d = await getMyItProcessData();
        if (d?.summaries) setMyItProcessData(d);
        return r;
      }}
      onGetAuthToken={async () => {
        try {
          const account = instance.getAllAccounts()[0] || accounts[0];
          if (!account) return null;
          const tokenResp = await instance.acquireTokenSilent({
            scopes: ['api://1660c30a-9608-404d-b6a3-3fee98626edf/access_as_user'],
            account,
          });
          return tokenResp.accessToken;
        } catch (e) {
          console.warn('onGetAuthToken refresh failed:', e.message);
          return null;
        }
      }}
      savedClientDomainOverrides={savedState?.clientDomainOverrides||{}}
      savedClientEmailOverrides={savedState?.clientEmailOverrides||{}}
      savedMeetingRawNotes={savedState?.meetingRawNotes||{}}
      onRefreshAtSync={handleRefreshAtSync}
      atDashData={atDashData}
      atSyncing={atSyncing}
      onLoadDayCanvas={handleLoadDayCanvas}
      onSaveDayCanvas={handleSaveDayCanvas}
      onSaveState={handleSaveState}
      onTriggerSync={handleTriggerSync}
      onExportCsv={handleExportCsv}
      onGetSyncStatus={handleGetSyncStatus}
      savedState={savedState}
      lastEmailRefresh={lastEmailRefresh}
      emailRefreshing={emailRefreshing}
      onRefreshEmails={async () => {
        try {
          setEmailRefreshing(true);
          const msgs = await getEmails();
          setLiveEmails(transformEmails(msgs));
          setLastEmailRefresh(new Date());
        } catch (e) {
          console.warn("Manual email refresh failed:", e.message);
        } finally {
          setEmailRefreshing(false);
        }
      }}
      syncStatus={syncStatus}
      onSignOut={handleSignOut}
      onForceLogin={handleForceLogin}
    />
  );
}
